// hooks/useTopUpRewards.js
import { useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt, estimateGas, getGasPrice } from 'wagmi/actions';
import { parseEther, formatEther } from 'viem';
import { childPool, unifarmFactory } from '../../../../utils/contract';
import { tokenAmount } from '../../../../utils/tokenAmount';

export const useTopUpRewards = () => {
    const [isTopUpInProgress, setIsTopUpInProgress] = useState(false);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const config = useConfig();

    const estimateTopUpGas = useCallback(
        async (poolContractAddress, amount,decimals) => {
            try {
                setIsEstimatingGas(true);
                const amountWei = tokenAmount(amount, decimals)?.parsed //  parseEther(amount?.toString());

                // Estimate gas for the transaction
                const estimatedGas = await estimateGas(config, {
                    address: poolContractAddress,
                    abi: childPool?.abi,
                    functionName: 'increaseRewardAmount',
                    args: [amountWei],
                });

                // Get current gas price
                const gasPrice = await getGasPrice(config);

                // Calculate total gas cost in wei
                const totalGasCost = estimatedGas * gasPrice;

                // Convert to ETH
                const totalGasCostETH = formatEther(totalGasCost);

                return {
                    estimatedGas: estimatedGas?.toString(),
                    gasPrice: gasPrice?.toString(),
                    totalGasCostWei: totalGasCost?.toString(),
                    totalGasCostETH: parseFloat(totalGasCostETH),
                    totalGasCostETHFormatted: totalGasCostETH
                };

            } catch (error) {
                console.error("Error estimating gas:", error);
                throw error;
            } finally {
                setIsEstimatingGas(false);
            }
        },
        [config]
    );

    const topUpRewards = useCallback(
        async (poolContractAddress, amount, decimals) => {
            try {
                setIsTopUpInProgress(true);

                const amountWei = tokenAmount(amount, decimals)?.parsed // parseEther(amount?.toString());

                const hash = await writeContract(config, {
                    address: poolContractAddress,
                    abi: childPool?.abi,
                    functionName: 'increaseRewardAmount',
                    args: [amountWei],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });

                // console.log("Top-up successful. Transaction hash:", hash);

                return { receipt, hash };

            } catch (error) {
                console.error("Error topping up rewards:", error);
                throw error;
            } finally {
                setIsTopUpInProgress(false);
            }
        },
        [config]
    );

    return {
        isTopUpInProgress,
        isEstimatingGas,
        topUpRewards,
        estimateTopUpGas
    };
};